// =============================================================================
// Log Collector Admin App
// =============================================================================

import { useState, useCallback } from 'react';
import { LogCollectorProvider } from './hooks/context.js';
import { useAuth } from './hooks/useAuth.js';
import { useApps } from './hooks/useApps.js';
import { useApiKeys } from './hooks/useApiKeys.js';
import { useStats } from './hooks/useStats.js';
import { useLogs } from './hooks/useLogs.js';
import { useAppStats } from './hooks/useAppStats.js';
import { useAggregations } from './hooks/useAggregations.js';
import { Dashboard } from './components/Dashboard.js';
import { useLogCollectorClient } from './hooks/useLogCollectorClient.js';
import type { ApiKeyPermission, ApiKeyWithKey, SearchParams, ExportFormat, AggregationConfigInput, AggregationFilters } from './lib/api.js';

// =============================================================================
// Dashboard Container
// =============================================================================

/**
 * Container component that wires up all the hooks to the Dashboard component.
 */
function DashboardContainer() {
  // Get the API client
  const { client } = useLogCollectorClient();

  // Auth hook
  const { isAuthenticated, isLoading: isLoggingIn, error: loginError, login, logout } = useAuth();

  // Stats hook
  const { stats, isLoading: isLoadingStats } = useStats({ fetchOnMount: isAuthenticated });

  // Apps hook
  const { apps, isLoading: isLoadingApps, error: appsError, createApp, deleteApp } = useApps({
    fetchOnMount: isAuthenticated,
  });

  // API Keys hook
  const { apiKeys, isLoading: isLoadingApiKeys, error: apiKeysError, createApiKey, deleteApiKey } =
    useApiKeys({ fetchOnMount: isAuthenticated });

  // Logs hook
  const {
    logs,
    total: logsTotal,
    hasMore: logsHasMore,
    isLoading: isLoadingLogs,
    error: logsError,
    search: searchLogs,
  } = useLogs();

  // Pagination state
  const [logsPage, setLogsPage] = useState(1);
  const [logsPageSize, setLogsPageSize] = useState(100);

  // Stats time range state
  const [statsAppId, setStatsAppId] = useState<string | null>(null);
  const [statsTimeRange, setStatsTimeRange] = useState<{ from: string; to: string }>(() => {
    const now = new Date();
    const from = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    return { from: from.toISOString(), to: now.toISOString() };
  });

  // App stats hook
  const {
    summary: appStatsSummary,
    timeline: appStatsTimeline,
    isLoading: isLoadingAppStats,
    error: appStatsError,
  } = useAppStats({
    appId: statsAppId,
    from: statsTimeRange.from,
    to: statsTimeRange.to,
  });

  // Aggregations state
  const [aggregationsAppId, setAggregationsAppId] = useState<string | null>(null);

  // Aggregations hook
  const {
    configs: aggregationConfigs,
    isLoading: isLoadingAggregations,
    error: aggregationsError,
    create: createAggregation,
    update: updateAggregation,
    remove: removeAggregation,
    compute: computeAggregation,
  } = useAggregations({ appId: aggregationsAppId });

  // Handlers
  const handleLogin = useCallback(async (password: string) => {
    await login(password);
  }, [login]);

  const handleLogout = useCallback(async () => {
    await logout();
  }, [logout]);

  const handleCreateApp = useCallback(async (name: string) => {
    await createApp(name);
  }, [createApp]);

  const handleDeleteApp = useCallback(async (appId: string) => {
    await deleteApp(appId);
  }, [deleteApp]);

  const handleCreateApiKey = useCallback(
    async (appId: string, permissions: ApiKeyPermission, rateLimit: number): Promise<ApiKeyWithKey> => {
      const key = await createApiKey(appId, permissions, rateLimit);
      if (!key) {
        throw new Error('Failed to create API key');
      }
      return key;
    },
    [createApiKey]
  );

  const handleDeleteApiKey = useCallback(async (keyId: string) => {
    await deleteApiKey(keyId);
  }, [deleteApiKey]);

  const handleSearchLogs = useCallback((params: SearchParams, appId: string) => {
    searchLogs(params, appId);
  }, [searchLogs]);

  const handleLogsPageChange = useCallback((page: number) => {
    setLogsPage(page);
  }, []);

  const handleLogsPageSizeChange = useCallback((pageSize: number) => {
    setLogsPageSize(pageSize);
  }, []);

  const handleExportLogs = useCallback(
    async (appId: string, format: ExportFormat, params: SearchParams): Promise<void> => {
      const result = await client.exportLogs(appId, format, params);
      client.downloadBlob(result.blob, result.filename);
    },
    [client]
  );

  const handleClearLogs = useCallback(
    async (appId: string, params?: SearchParams): Promise<void> => {
      if (params) {
        await client.clearLogs(appId, params);
      } else {
        await client.clearAllLogs(appId);
      }
    },
    [client]
  );

  const handleStatsAppIdChange = useCallback((appId: string | null) => {
    setStatsAppId(appId);
  }, []);

  const handleStatsTimeRangeChange = useCallback((from: string, to: string) => {
    setStatsTimeRange({ from, to });
  }, []);

  const handleAggregationAppIdChange = useCallback((appId: string | null) => {
    setAggregationsAppId(appId);
  }, []);

  const handleCreateAggregation = useCallback(
    async (appId: string, config: AggregationConfigInput) => {
      await createAggregation(appId, config);
    },
    [createAggregation]
  );

  const handleUpdateAggregation = useCallback(
    async (appId: string, configId: string, config: Partial<AggregationConfigInput>) => {
      await updateAggregation(appId, configId, config);
    },
    [updateAggregation]
  );

  const handleDeleteAggregation = useCallback(
    async (appId: string, configId: string) => {
      await removeAggregation(appId, configId);
    },
    [removeAggregation]
  );

  const handleComputeAggregation = useCallback(
    async (appId: string, configId: string, from: string, to: string, filters?: AggregationFilters | null) => {
      return computeAggregation(appId, configId, from, to, filters);
    },
    [computeAggregation]
  );

  return (
    <Dashboard
      // Auth props
      isAuthenticated={isAuthenticated}
      onLogin={handleLogin}
      onLogout={handleLogout}
      loginError={loginError}
      isLoggingIn={isLoggingIn}
      // Stats props
      stats={stats}
      isLoadingStats={isLoadingStats}
      // Apps props
      apps={apps}
      onCreateApp={handleCreateApp}
      onDeleteApp={handleDeleteApp}
      isLoadingApps={isLoadingApps}
      appsError={appsError}
      // API Keys props
      apiKeys={apiKeys}
      onCreateApiKey={handleCreateApiKey}
      onDeleteApiKey={handleDeleteApiKey}
      isLoadingApiKeys={isLoadingApiKeys}
      apiKeysError={apiKeysError}
      // Logs props
      logs={logs}
      logsTotal={logsTotal}
      logsHasMore={logsHasMore}
      onSearchLogs={handleSearchLogs}
      isLoadingLogs={isLoadingLogs}
      logsError={logsError}
      logsPage={logsPage}
      logsPageSize={logsPageSize}
      onLogsPageChange={handleLogsPageChange}
      onLogsPageSizeChange={handleLogsPageSizeChange}
      onExportLogs={handleExportLogs}
      onClearLogs={handleClearLogs}
      // App Stats props
      appStatsSummary={appStatsSummary}
      appStatsTimeline={appStatsTimeline}
      isLoadingAppStats={isLoadingAppStats}
      appStatsError={appStatsError}
      statsTimeRange={statsTimeRange}
      onStatsTimeRangeChange={handleStatsTimeRangeChange}
      onStatsAppIdChange={handleStatsAppIdChange}
      // Aggregation props
      aggregationConfigs={aggregationConfigs}
      isLoadingAggregations={isLoadingAggregations}
      aggregationsError={aggregationsError}
      onCreateAggregation={handleCreateAggregation}
      onUpdateAggregation={handleUpdateAggregation}
      onDeleteAggregation={handleDeleteAggregation}
      onComputeAggregation={handleComputeAggregation}
      onAggregationAppIdChange={handleAggregationAppIdChange}
    />
  );
}

// =============================================================================
// App Root
// =============================================================================

export default function App() {
  return (
    <LogCollectorProvider baseUrl="/api">
      <DashboardContainer />
    </LogCollectorProvider>
  );
}
